import { getTimeZoneAbbreviation, getTimeZoneName } from "@cp/toolkit";
import { FieldDatePicker, FieldInput, Group, Icon, type OpenFormOptions, useOpenForm } from "@qw/ui";
import { add, format, startOfDay } from "date-fns";
import { type FC, useCallback, useMemo } from "react";
import { z } from "zod";

const ScheduleSendDraftFormSchema = z
  .object({
    scheduledSendAtDate: z.date({ message: "Please enter a valid date" }),
    scheduledSendAtTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, { message: "Please enter a valid time" }),
  })
  .refine((data) => getScheduledSendAt(data.scheduledSendAtDate, data.scheduledSendAtTime) > new Date(), {
    message: "Scheduled time must be in the future",
    path: ["scheduledSendAtTime"],
  });

const ScheduleSendDraftFormFields: FC = () => {
  // Create a stable reference to today's date (at midnight) to prevent the disabled boundary from shifting
  const today = useMemo(() => startOfDay(new Date()), []);
  const timezone = useMemo(() => getTimeZoneName(today), [today]);
  const timezoneAbbreviation = useMemo(() => getTimeZoneAbbreviation(today), [today]);

  return (
    <Group className="gap-1.5">
      <Group direction="row" type="flex">
        <FieldDatePicker
          label="Date"
          name="scheduledSendAtDate"
          inputProps={{
            calendarProps: {
              disabled: [{ before: today }],
              fromYear: today.getFullYear(),
              toYear: today.getFullYear() + 5,
            },
          }}
        />
        <FieldInput label="Time" name="scheduledSendAtTime" type="time" inputProps={{ step: 60 * 15 }} />
      </Group>
      <p className="text-sm text-muted-foreground">
        Scheduled time will be in{" "}
        <strong>
          {timezone} ({timezoneAbbreviation})
        </strong>
        .
      </p>
    </Group>
  );
};

type ScheduleSendDraftFormOptions = Partial<OpenFormOptions<typeof ScheduleSendDraftFormSchema>>;

export const useScheduleSendDraftForm = () => {
  const { openForm } = useOpenForm();

  const openScheduleSendDraftForm = useCallback(
    async (options?: ScheduleSendDraftFormOptions) => {
      const formValues = await openForm(<ScheduleSendDraftFormFields />, {
        title: "Schedule send draft",
        description: "Select the date and time to schedule the draft to be sent.",
        submitButtonProps: {
          children: (
            <>
              <Icon icon="schedule_send" />
              Schedule send
            </>
          ),
        },
        validationSchema: ScheduleSendDraftFormSchema,
        defaultValues: {
          scheduledSendAtDate: new Date(),
          scheduledSendAtTime: format(add(new Date(), { minutes: 10 }), "HH:mm"),
        },
        ...options,
      });

      if (!formValues) {
        return;
      }

      return getScheduledSendAt(formValues.scheduledSendAtDate, formValues.scheduledSendAtTime);
    },
    [openForm]
  );

  return { openScheduleSendDraftForm };
};

const getScheduledSendAt = (scheduledSendAtDate: Date, scheduledSendAtTime: string): Date => {
  const date = new Date(scheduledSendAtDate);
  date.setHours(Number(scheduledSendAtTime.split(":")[0]), Number(scheduledSendAtTime.split(":")[1]), 0, 0);
  return date;
};
